<template>
  <div
    class="stays-section"
    :class="[
      group.singleStayType && 'stays-section--solo-rate',
      group.isDealOfTheDay() && 'stays-section--deal-of-the-day'
    ]"
  >
    <div class="stays-section__meals">
      <MealPlansList
        :meals="meals"
        :active="selectedStayId"
        @selectedMeal="setActiveStay"
        @isRoomOnly="$emit('isRoomOnly', $event)"
      />
    </div>
  </div>
</template>

<script>
import MealPlansList from './MealPlansList'
import Group from '@/multi-room-display/domain/Group'
import TabTypes from '@/domain/TabTypes'
import { isMobileDevice } from '@/infrastructure/Utils'
import { createNamespacedHelpers } from 'vuex'
const { mapState } = createNamespacedHelpers('multiRoomDisplay')

export default {
  name: 'StaysSection',
  components: {
    MealPlansList
  },
  props: {
    group: {
      type: Group
    },
    tabToSelect: {
      type: String,
      defaultValue: TabTypes.MEMBER
    },
    currentGroupPublicRates: {
      type: Array
    },
    currentGroupMemberRates: {
      type: Array
    }
  },
  data: () => ({
    selectedMemberStay: null,
    selectedPublicStay: null,
    selectedStayId: '',
    isMobile: isMobileDevice(),
    selectedTab: '',
    TabTypes: TabTypes,
    mealCode: null
  }),
  created() {
    this.$emit('mealsInfo', this.meals.length === 2)
    this.selectedMemberStay = this.group.staysMember[0]
    this.selectedPublicStay = this.group.staysPublic[0]
    this.selectedTab = this.tabToSelect
    this.selectedStayId =
      this.currentGroupPublicRates.some(rate => rate === this.selectedId) ||
      this.currentGroupMemberRates.some(rate => rate === this.selectedId)
        ? this.selectedId
        : this.filteredStays[0].id
    this.mealCode = this.filteredStays[0].mealPlan
    this.setActiveStay(this.selectedStayId, this.mealCode)
  },
  computed: {
    ...mapState(['selectedId']),
    filteredStays() {
      return this.group.singleStayType
        ? [...this.group.staysMember, ...this.group.staysPublic]
        : this.selectedTab === TabTypes.MEMBER
          ? this.group.staysMember
          : this.group.staysPublic
    },
    meals() {
      return this.filteredStays.map(stays => stays.mealPlanDto)
    }
  },
  methods: {
    getMealPlanRate(mealPlanRates) {
      const mealPLan = mealPlanRates.find(rate => rate.mealPlan === this.mealCode)
      if (mealPLan) {
        this.setActiveStay(mealPLan.id, this.mealCode)
      }
      return mealPLan
    },
    setActiveStay(stayId, code) {
      const activeStay = this.group.findStayById(stayId)
      this.mealCode = code
      this.selectedStayId = activeStay.id
      if (this.selectedTab === TabTypes.MEMBER) {
        this.selectedMemberStay = activeStay
      } else {
        this.selectedPublicStay = activeStay
      }
      this.$emit('activeStay', activeStay)
    }
  },
  watch: {
    filteredStays(newStays, oldStays) {
      if (newStays.length !== oldStays.length) {
        this.selectedMemberStay = this.group.staysMember[0]
        this.selectedPublicStay = this.group.staysPublic[0]
        const stayFiltered = newStays[0]
        this.selectedStayId = stayFiltered?.id || ''
        if (stayFiltered) {
          this.setActiveStay(this.selectedStayId, stayFiltered.code)
        }
      }
    },
    tabToSelect(newStays) {
      this.selectedTab = newStays
      let mealPlanSelected
      if (this.selectedTab === TabTypes.MEMBER) {
        mealPlanSelected = this.getMealPlanRate(this.group.staysMember)
      } else {
        mealPlanSelected = this.getMealPlanRate(this.group.staysPublic)
      }
      this.selectedStayId = mealPlanSelected ? mealPlanSelected.id : this.filteredStays[0].id
      this.setActiveStay(this.selectedStayId, this.mealCode)
    }
  }
}
</script>

<style lang="scss">
$tab-b-spacing: 0.75rem;
$tab-t-spacing: 0.6rem;
$tab-h-spacing: 0.7rem;
$tab-v-padding: 1.7rem;

$tab-radius: 0.5rem;

.stays-section {
  margin: 0;
  width: 100%;

  @media (min-width: 992px) {
    margin-bottom: 1.2rem;
  }

  &__tabs {
    display: flex;
    flex-direction: row;
    margin-bottom: -1px;
    cursor: pointer;
    margin-right: -#{$tab-h-spacing};
  }

  &__tab {
    flex: 1;
    display: flex;
    align-items: center;
    flex-direction: column;
    padding: $tab-v-padding 1.2rem;
    border-radius: $tab-radius;
    margin-right: $tab-h-spacing;
    margin-top: $tab-t-spacing;
    margin-bottom: $tab-b-spacing;
    background-color: #f8f8f8;
    border: 1px solid transparent;
    opacity: 0.7;

    @media (min-width: 992px) {
      flex-direction: row;
      justify-content: space-between;
      align-items: start;
      &--discounted {
        align-items: center;
      }
    }

    &--selected {
      background-color: white;
      margin-top: 0;
      margin-bottom: 0;
      padding-bottom: calc(#{$tab-v-padding} + #{$tab-b-spacing});
      border: 1px solid #d8d8d8;
      border-top: solid $tab-t-spacing #c00d0d;
      border-bottom-color: transparent;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      opacity: 1;
    }
  }

  &__tab:hover {
    border: 1px solid #d8d8d8;
    background-color: white;
    opacity: 1;
  }

  &__tab--selected:hover {
    border-top: solid $tab-t-spacing #c00d0d;
    border-bottom-color: transparent;
    background-color: white;
  }

  &__tab-title {
    font-weight: bold;
    text-align: center;
    @media (min-width: 992px) {
      text-align: left;
      white-space: nowrap;
    }
  }

  &__price {
    display: flex;
    flex-direction: column;
    align-items: center;

    @media (min-width: 992px) {
      align-items: flex-end;
    }
  }

  /*  &__meals {

    @media (min-width: 992px) {
    }
  } */

  &--solo-rate {
    .stays-section {
      &__meals {
        border-radius: $tab-radius;
      }
    }
  }

  &--deal-of-the-day {
    .stays-section__tab--selected {
      border-top-color: #e6913c;
    }
  }
}
</style>
